<script setup>
import { ref, onMounted, computed, watchEffect } from 'vue';
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import axios from 'axios';
import { authHeader } from '../../helpers/authservice/auth-header';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import store from '../../state/store';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import _ from 'lodash';
import '@jobinsjp/vue3-datatable/dist/style.css';
import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';


const title = ref('Camera Report');

const items = ref([
  {
    text: "Home",
  },
    {
    text: "Reports",
  },
  {
    text: "Camera Report",
    active: true,
  },
]);

const baseURL = computed(() => axios.defaults.baseURL);

const isGenerate = ref(false);

const humanfriendlyConfig = ref({
  altInput: true,
  altFormat: "F j, Y",
  dateFormat: "Y-m-d"
});

const searchParams = ref({
  camera_id: 'all',
  from: '',
  to: '',
});

const generatedResults = ref([]);

const cameras = ref([]);

const loading = computed(() => store.state.globals.loading);

const users = ref([]);

const userResult  = ref(null);

const setSelected = async () => {
    userResult.value = await users.value.find(user => user.id === searchParams.value.user_id);
}

const getCameras = async (search, size) => {
  try {
    const { data } = await axios.get(`/cameras`, {
      headers: authHeader(),
      params: {
        search: search === '' ? '' : encodeURIComponent(search),
        pageSize: size
      }
    });

    data.data.unshift({ id: 'all', camera_id: 'all', location: 'All Cameras'});
    return data.data;
  } catch (err) {
    console.log(err);
  }
}

const searchQuery =  _.debounce( async  (loading = false, search = '') => {
  const data = await getCameras(search, 20);
  cameras.value = data;
  if (loading) {
    loading(false)
  }
}, 500)

const onSearch = (search, loading) => {
  if (search.length) {
    loading(true);
    searchQuery(loading, search);
  }
}

const generateReport = async () => {
  isGenerate.value = false;

  store.dispatch('globals/loading', null, { root: true });
  generatedResults.value = [];

  try {
    const { data } = await axios.get(`/report/camera?${Object.keys(searchParams.value).map(key => `${key}=${searchParams.value[key]}`).join('&')}&raw=true`, {
      headers: authHeader()
    })

    generatedResults.value = data;
    isGenerate.value = true
    store.dispatch('globals/loaded', null, { root: true });
  } catch (err) {
    console.log(err);
    isGenerate.value = true
    store.dispatch('globals/loaded', null, { root: true });
  }
}

onMounted(async () => {
    const data = await getCameras('', 30);
    cameras.value = data;
})

watchEffect(()=>{
    if (searchParams.value.camera_id) {
        setSelected();
    }
});
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
        <div class="container-fluid">
          <div class="row">
              <div class="col-xs-12 col-md-6 col-xl-4">
                <b-overlay :show="loading">
                    <div class="card">
                      <div class="card-body">
                          <form action="">
                              <div class="mb-3 text-center">
                                <label for="search-status-*" class="form-label">Camera</label>
                                <v-select 
                                    @change="setSelected" 
                                    placeholder="Select Camera" 
                                     :get-option-label="(option) => {
                                      if (typeof option.location === 'undefined' && typeof option.camera_id === 'undefined') return '';

                                      return `${option.location} (ID: ${option.camera_id})`
                                    }" 
                                    :reduce="cameras => cameras.id" 
                                    :filterable="false" 
                                    :options="cameras"
                                    @search="onSearch" 
                                    v-model="searchParams.camera_id"
                                    class="fs-11"
                                  >
                                </v-select>
                              </div>
                              <div class="mb-3 text-center">
                                <label for="violation_date" class="form-label">Date of Upload</label>
                                <div class="d-flex">
                                  <flatPickr
                                    id="date"
                                    placeholder="From"
                                    v-model="searchParams.from"
                                    :config="humanfriendlyConfig"
                                    class="form-control flatpickr-input mr-1"
                                  ></flatPickr>
                                  <flatPickr
                                    id="date"
                                    placeholder="To"
                                    v-model="searchParams.to"
                                    :config="humanfriendlyConfig"
                                    class="form-control flatpickr-input ml-1"
                                  ></flatPickr>
                                </div>
                              </div>
                              <div class="row justify-content-around">
                                  <button type="button" @click.prevent="generateReport" class="col-xs-12 col-xl-5 btn btn-info btn-sm my-1 fs-10">
                                    <i class="mdi mdi-table-large align-middle mr-1 fs-12 text-white"></i>
                                    Generate Report
                                  </button>
                                  <a :href="`${baseURL}/report/camera?${Object.keys(searchParams).map(key => `${key}=${searchParams[key]}`).join('&')}`" download target="_blank" class="col-xs-12 col-xl-5 btn btn-info btn-sm my-1 fs-10">
                                    <i class="mdi mdi-microsoft-excel align-middle mr-1 fs-12 text-white"></i>
                                    Export Summary
                                  </a>
                              </div>
                          </form>
                      </div>
                    </div>
                </b-overlay>
              </div>
              <div v-if="isGenerate" class="col-xs-12 col-xl-8">
                <b-overlay :show="loading">
                  <div class="card">
                    <div class="card-header">
                      <div class="card-title">
                        <h4>Generation results</h4>
                      </div>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col lg-12">
                            <div class="text-muted">* Note: Traffic Records Count are based on the Date of Upload generated by the user.</div>
                            <data-table
                                :rows="generatedResults"
                                striped
                                :loading="loading"
                                filter
                                hoverable
                                >
                                <template #thead>
                                    <table-head>Camera ID</table-head>
                                    <table-head>LGU</table-head>
                                    <table-head>Location</table-head>
                                    <table-head>Status</table-head>
                                    <table-head>Camera Type</table-head>
                                    <table-head>Traffic Records Count</table-head>
                                </template>

                                <template #tbody="{row}">
                                    <table-body>{{ row.camera_id ? row.camera_id : 'n/a' }}</table-body>
                                    <table-body>{{ row.location ? row.location : 'n/a' }}</table-body>
                                    <table-body>{{ row.lgu ? row.lgu.name : 'n/a' }}</table-body>
                                    <table-body :class="row.is_enabled ? 'text-success' : 'text-danger'">{{ row.is_enabled ? 'Active' : 'Inactive' }}</table-body>
                                    <table-body>{{ row.camera_type ? row.camera_type.name : 'n/a' }}</table-body>
                                    <table-body>{{ row.traffic_records_count ? row.traffic_records_count : 0 }}</table-body>
                                </template>

                                <template #empty>
                                <TableBodyCell colspan="10">
                                    No generated results found.
                                </TableBodyCell>
                            </template>
                            </data-table>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-overlay> 
              </div>
              <div v-else class="col-xs-12 col-xl-8 text-center">
                <b-overlay :show="loading">
                  <div class="card">
                    <div class="card-body">
                      <div class="row justify-content-center">
                        <div class="col-lg-9">
                          <h4 class="mt-4 fw-semibold">Camera Report</h4>
                          <p class="text-muted mt-3 fs-5"> 
                            Generated Reports will show up here
                          </p>
                        </div>
                      </div>
                      <div class="row justify-content-center mt-5 mb-2">
                        <div class="col-sm-7 col-8">
                          <img src="@/assets/images/file.png" alt="" class="img-fluid" width="400">
                        </div>
                      </div>
                    </div>
                  </div>
                </b-overlay>
              </div>
          </div>
        </div>
  </Layout>
</template>
